import { useTranslation } from 'react-i18next';
import { Meta } from '@/components/MetaManager';
import { PageLayout } from '@/components/PageLayout';
import { Shield, Eye, Database, Lock, Mail, Globe } from 'lucide-react';

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const lastUpdated = '2025-01-28';

  const sections = isZh ? [
    {
      icon: Eye,
      title: '信息收集',
      content: `我们可能收集以下类型的信息：
• **个人信息**：当您通过联系表单与我们沟通时，我们可能收集您的姓名和电子邮件地址。
• **使用数据**：我们使用 Google Analytics 和 Microsoft Clarity 收集匿名的网站使用统计数据，包括页面浏览量、访问时长和设备信息。
• **技术数据**：包括 IP 地址、浏览器类型、操作系统等技术信息。`
    },
    {
      icon: Database,
      title: '信息使用',
      content: `我们使用收集的信息用于：
• 改善网站内容和用户体验
• 分析网站流量和使用模式
• 回复您的咨询和请求
• 发送与我们服务相关的通知（仅在您同意的情况下）`
    },
    {
      icon: Lock,
      title: '数据安全',
      content: `我们采取合理的技术和组织措施来保护您的个人信息：
• 使用 HTTPS 加密传输数据
• 限制对个人数据的访问权限
• 定期审查我们的数据收集和存储实践
• 我们不会出售、交易或以其他方式转让您的个人信息给第三方`
    },
    {
      icon: Globe,
      title: 'Cookie 政策',
      content: `我们的网站使用 Cookie 来：
• 分析网站流量（Google Analytics）
• 了解用户行为（Microsoft Clarity）
• 记住您的语言偏好

您可以通过浏览器设置禁用 Cookie，但这可能会影响网站的某些功能。`
    },
    {
      icon: Shield,
      title: '您的权利',
      content: `根据适用的数据保护法律，您可能拥有以下权利：
• 访问我们持有的关于您的个人数据
• 要求更正不准确的数据
• 要求删除您的个人数据
• 反对或限制数据处理
• 数据可携带性

如需行使这些权利，请通过下方联系方式与我们联系。`
    },
    {
      icon: Mail,
      title: '联系我们',
      content: `如果您对本隐私政策有任何疑问，请通过以下方式联系我们：
• 网站：https://borninsea.com
• Twitter：@wanghaisheng

我们将在合理时间内回复您的请求。`
    }
  ] : [
    {
      icon: Eye,
      title: 'Information Collection',
      content: `We may collect the following types of information:
• **Personal Information**: When you contact us through our contact form, we may collect your name and email address.
• **Usage Data**: We use Google Analytics and Microsoft Clarity to collect anonymous website usage statistics, including page views, visit duration, and device information.
• **Technical Data**: Including IP address, browser type, operating system, and other technical information.`
    },
    {
      icon: Database,
      title: 'Use of Information',
      content: `We use the collected information to:
• Improve website content and user experience
• Analyze website traffic and usage patterns
• Respond to your inquiries and requests
• Send notifications related to our services (only with your consent)`
    },
    {
      icon: Lock,
      title: 'Data Security',
      content: `We take reasonable technical and organizational measures to protect your personal information:
• Use HTTPS encryption for data transmission
• Limit access to personal data
• Regularly review our data collection and storage practices
• We do not sell, trade, or otherwise transfer your personal information to third parties`
    },
    {
      icon: Globe,
      title: 'Cookie Policy',
      content: `Our website uses cookies to:
• Analyze website traffic (Google Analytics)
• Understand user behavior (Microsoft Clarity)
• Remember your language preferences

You can disable cookies through your browser settings, but this may affect some website functionality.`
    },
    {
      icon: Shield,
      title: 'Your Rights',
      content: `Under applicable data protection laws, you may have the following rights:
• Access the personal data we hold about you
• Request correction of inaccurate data
• Request deletion of your personal data
• Object to or restrict data processing
• Data portability

To exercise these rights, please contact us using the information below.`
    },
    {
      icon: Mail,
      title: 'Contact Us',
      content: `If you have any questions about this Privacy Policy, please contact us:
• Website: https://borninsea.com
• Twitter: @wanghaisheng

We will respond to your request within a reasonable time.`
    }
  ];

  return (
    <PageLayout>
      <Meta
        title={isZh ? '隐私政策 | borninsea' : 'Privacy Policy | borninsea'}
        description={isZh ? '了解 borninsea 如何收集、使用和保护您的个人信息。' : 'Learn how borninsea collects, uses, and protects your personal information.'}
      />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {isZh ? '隐私政策' : 'Privacy Policy'}
            </h1>
            <p className="text-muted-foreground">
              {isZh ? '最后更新：' : 'Last Updated: '}{lastUpdated}
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
            <p className="text-lg text-muted-foreground leading-relaxed">
              {isZh 
                ? '欢迎访问 borninsea.com（以下简称"本网站"）。我们非常重视您的隐私，本隐私政策旨在说明我们如何收集、使用、披露和保护您的个人信息。使用本网站即表示您同意本隐私政策中描述的做法。'
                : 'Welcome to borninsea.com (the "Website"). We take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and protect your personal information. By using this Website, you agree to the practices described in this Privacy Policy.'}
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold mb-3">{section.title}</h2>
                    <div className="text-muted-foreground whitespace-pre-line leading-relaxed">
                      {section.content.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-12 p-6 bg-muted/50 rounded-xl text-center">
            <p className="text-sm text-muted-foreground">
              {isZh 
                ? '本隐私政策可能会不时更新。我们建议您定期查看此页面以了解任何变更。'
                : 'This Privacy Policy may be updated from time to time. We recommend that you review this page periodically for any changes.'}
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
